import React, { FC } from "react";
import { IconAlertGlyph } from "./panels";
import { plateFor } from "./PanelVideo";

/**
 * Page-level states.
 *
 * Both replace behaviour that had no UI at all: the detail view used to render
 * a 500x500 Lottie over the entire page while two serialized requests resolved,
 * and to call `onClose()` on ANY failure — a 404, a 403 or a network blip all
 * silently dumped you back on the list with no message.
 */

const Shimmer: FC<{ className?: string; style?: React.CSSProperties }> = ({ className, style }) => (
  <div className={`bg-track animate-pulse motion-reduce:animate-none ${className || ""}`} style={style} />
);

/** The same 32px back control the loaded header renders, so it is reachable while
 *  the two blocking requests resolve — before, it mounted only with `video`. */
const BackButton: FC<{ onClick: () => void; size?: number }> = ({ onClick, size = 32 }) => (
  <button
    type="button"
    aria-label="Back"
    onClick={onClick}
    className="shrink-0 border border-border-token rounded-lg bg-surface text-text-2 inline-flex items-center justify-center hover:text-text-1 hover:border-accent transition-colors"
    style={{ width: size, height: size }}
  >
    <svg viewBox="0 0 24 24" width={size > 28 ? 17 : 15} height={size > 28 ? 17 : 15} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="m14 6-6 6 6 6" />
    </svg>
  </button>
);

/** The nested host's 44px identity bar while loading: back plus a title stand-in.
 *  `NestedModuleBar` needs the video to render, so nothing stood here before. */
export const NestedLoadingBar: FC<{ onBack?: () => void }> = ({ onBack }) => (
  <div className="flex items-center gap-2.5 px-4 border-b border-border-token bg-surface min-h-[44px] py-2">
    {onBack && <BackButton onClick={onBack} size={28} />}
    <Shimmer className="rounded" style={{ width: 220, height: 15 }} />
  </div>
);

interface SkeletonProps {
  /**
   * The video's `aspect_ratio`, when the host already has it — the inline
   * hosts opened this view from a loaded list. Without it the plate is
   * landscape, which is unknowable-but-honest on a cold load and a visible
   * snap on a portrait video that the host could have avoided.
   */
  aspectHint?: string;
  onBack?: () => void;
  nested?: boolean;
}

/** Skeleton in the real geometry of the loaded page — plate, identity, panels. */
export const DetailSkeleton: FC<SkeletonProps> = ({ aspectHint, onBack, nested = false }) => {
  const plate = plateFor(aspectHint, nested);
  return (
    <div className="flex flex-col gap-5">
      {/* The breadcrumb is suppressed when nested; so is its stand-in. */}
      {!nested && <Shimmer className="rounded" style={{ width: 320, height: 14 }} />}

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <Shimmer
          className="rounded-xl w-full shrink-0"
          style={{ maxWidth: plate.w, aspectRatio: `${plate.w} / ${plate.h}` }}
        />
        <div className="flex-1 min-w-0 w-full flex flex-col gap-3.5">
          {!nested && (
            <div className="flex items-center gap-2">
              {onBack && <BackButton onClick={onBack} />}
              <Shimmer className="rounded" style={{ width: "55%", height: 30 }} />
            </div>
          )}
          <Shimmer className="rounded" style={{ width: "35%", height: 12 }} />
          {!nested && (
            <div className="flex gap-2.5">
              <Shimmer className="rounded-xl" style={{ width: 150, height: 40 }} />
              <Shimmer className="rounded-xl" style={{ width: 150, height: 40 }} />
              <Shimmer className="rounded-xl" style={{ width: 40, height: 40 }} />
            </div>
          )}
          <Shimmer className="rounded-xl w-full" style={{ height: 132 }} />
        </div>
      </div>

      {/* The jump rail is a full-width row below the identity row, not inside
          the identity column where the old stand-in put it. */}
      <div className="flex gap-2">
        {[106, 96, 116, 112, 96].map((w, i) => (
          <Shimmer key={i} className="rounded-full" style={{ width: w, height: 28 }} />
        ))}
      </div>

      {/* Page score leads the page, so it leads the skeleton — a placeholder in
          the old order made the blocks visibly re-shuffle when content landed. */}
      <Shimmer className="rounded-xl w-full" style={{ height: 172 }} />
      <Shimmer className="rounded-xl w-full" style={{ height: 420 }} />
      <div
        className="grid gap-5"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}
      >
        {[0, 1, 2].map((i) => (
          <Shimmer key={i} className="rounded-xl w-full" style={{ height: 260 }} />
        ))}
      </div>
    </div>
  );
};

interface NotFoundProps {
  kind: "notfound" | "forbidden" | "error";
  onBack?: () => void;
  onRetry?: () => void;
  /** Printed under the block so a bad link can be reported verbatim. */
  videoId?: number;
}

const COPY: Record<NotFoundProps["kind"], { title: string; body: string }> = {
  notfound: {
    title: "Video not found",
    body: "It may have been removed, or you may not have access to it. Nothing was deleted from your list.",
  },
  forbidden: {
    title: "You don't have access to this video",
    body: "Ask an account manager if you think you should. Nothing was deleted from your list.",
  },
  error: {
    title: "Couldn't load this video",
    body: "Something went wrong on our end. Try again in a moment.",
  },
};

export const DetailNotFound: FC<NotFoundProps> = ({ kind, onBack, onRetry, videoId }) => {
  const copy = COPY[kind];
  return (
    <div
      className="bg-surface border border-border-token rounded-xl flex flex-col items-center justify-center text-center gap-2.5 px-10 py-16"
      style={{ minHeight: 420, boxShadow: "var(--sh-1)" }}
    >
      <span className="w-14 h-14 rounded-full bg-tint text-accent-text inline-flex items-center justify-center">
        <IconAlertGlyph size={26} strokeWidth={1.6} />
      </span>
      <h1 className="m-0 font-CircularStdBlack text-text-1" style={{ fontSize: 28, lineHeight: 1.15 }}>
        {copy.title}
      </h1>
      <p className="m-0 text-text-2" style={{ fontSize: 13.5, lineHeight: 1.5, maxWidth: 420 }}>
        {copy.body}
      </p>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2.5">
        {onBack && (
          // "Go back", not the handoff's "Back to videos": on the expert profile
          // and product detail this page is nested, and back returns you to that
          // host's grid rather than to /videos.
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center h-10 px-4 rounded-xl bg-surface-2 text-text-1 hover:bg-track transition-colors"
            style={{ fontSize: 13.5 }}
          >
            Go back
          </button>
        )}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center h-10 px-4 rounded-xl bg-surface border border-track text-text-1 hover:border-muted transition-colors"
            style={{ fontSize: 13.5 }}
          >
            Try again
          </button>
        )}
      </div>
      {videoId != null && (
        <div className="text-muted tabular-nums" style={{ fontSize: 11.5, marginTop: 4 }}>
          ?video={videoId}
        </div>
      )}
    </div>
  );
};
